<script>
/**
 * @component MyProcedures
 * @description UC-REG-160 — Vista del paciente/prestador. Lista sus trámites +
 *              botón "Nuevo trámite". Al abrir un trámite ve el detalle con
 *              chat de ida y vuelta (mensajes del staff + los suyos). Puede
 *              agregar mensajes mientras el trámite no esté cerrado.
 *
 * @example <MyProcedures />
 */
import procedureService, { procedureTypeService } from "@/services/procedure";
import {
  formatProcedureAppointment,
  canCancelProcedureAppointment,
  procedureCancelErrorMessage,
  PROCEDURE_CANCEL_DIALOG,
} from "@/services/procedure-appointment";
import {
  PREFERRED_DOCTOR_MAX,
  REFERRAL_ACCEPT,
  REFERRAL_FIELD,
  childrenOf,
  fileKindLabel,
  isReferralFile,
  normalizePreferredDoctor,
  requestText,
  requiresReferral,
  requiresAppointment,
  APPOINTMENT_PREFERENCES,
  priorityText,
  resolveSelectedType,
  topLevelTypes,
  typeLabel,
} from "@/services/procedure-types-view";
import { isProcedurePreferredDoctorEnabled } from "@/services/config";
import { format } from "date-fns";
import { mapGetters } from "vuex";

/**
 * @description Form vacío de "Nuevo trámite". UC-REG-297: tipología +
 *   subtipo; UC-REG-298: médico de preferencia; UC-REG-299: derivación.
 * @returns {Object}
 */
const emptyNewForm = () => ({
  procedureType: "",
  procedureSubtype: "",
  description: "",
  preferredDoctor: "",
  // UC-REG-316: prioridad del turno (solo tipos con turno; default la primera).
  appointmentPreference: APPOINTMENT_PREFERENCES[0].value,
  files: [],
  referralFiles: [],
});

export default {
  name: "MyProcedures",

  data() {
    return {
      procedures: [],
      types: [],
      loading: false,

      // UC-REG-298 (Campana): el form pide "Médico de preferencia (opcional)"
      // en lugar de "Descripción *". Flag de build.
      preferredDoctorMode: isProcedurePreferredDoctorEnabled,
      preferredDoctorMax: PREFERRED_DOCTOR_MAX,
      /** @type {Array<{value:string,label:string}>} UC-REG-316 */
      appointmentPreferences: APPOINTMENT_PREFERENCES,
      referralAccept: REFERRAL_ACCEPT,

      newModal: {
        visible: false,
        saving: false,
        form: emptyNewForm(),
      },

      viewModal: {
        visible: false,
        procedure: null,
        newMessage: "",
        // Adjuntos a mandar con el próximo mensaje. Se van acumulando en
        // chips y se envían todos juntos al hacer "Enviar".
        newFiles: [],
        sending: false,
      },
    };
  },

  computed: {
    ...mapGetters(["user"]),

    /**
     * @description UC-REG-297 — Tipologías (tipos de primer nivel) del select.
     * @returns {Array<Object>}
     */
    topTypes() {
      return topLevelTypes(this.types);
    },

    /**
     * @description UC-REG-297 — Subtipos de la tipología elegida (vacío si
     *   no tiene → el select de subtipo no se muestra).
     * @returns {Array<Object>}
     */
    subtypes() {
      return childrenOf(this.types, this.newModal.form.procedureType);
    },

    /**
     * @description Tipo final de la solicitud (subtipo si la tipología tiene
     *   hijos, si no la tipología) o '' mientras falte elegir.
     * @returns {string}
     */
    selectedTypeId() {
      const { procedureType, procedureSubtype } = this.newModal.form;
      const r = resolveSelectedType({
        types: this.types,
        typeId: procedureType,
        subtypeId: procedureSubtype,
      });
      return r.ok ? r.typeId : "";
    },

    /**
     * @description UC-REG-299 — El tipo elegido exige la orden de derivación.
     * @returns {boolean}
     */
    needsReferral() {
      return requiresReferral(this.types, this.selectedTypeId);
    },

    /**
     * @description UC-REG-316 — Con el flag de Campana y un tipo que requiere
     *   turno, el form muestra los radios de prioridad.
     * @returns {boolean}
     */
    showPriority() {
      return (
        this.preferredDoctorMode &&
        requiresAppointment(this.types, this.selectedTypeId)
      );
    },

    /**
     * @description UC-REG-316 — El vecino eligió "Médico de preferencia".
     * @returns {boolean}
     */
    wantsPreferredDoctor() {
      return (
        this.showPriority &&
        this.newModal.form.appointmentPreference === "preferred-doctor"
      );
    },
  },

  created() {
    this.load();
  },

  methods: {
    priorityText,
    async load() {
      this.loading = true;
      try {
        const [mine, activeTypes] = await Promise.all([
          procedureService.mine(),
          procedureTypeService.list(),
        ]);
        this.procedures = mine;
        this.types = activeTypes;
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cargar la información"
        );
      } finally {
        this.loading = false;
      }
    },

    openNew() {
      if (this.types.length === 0) {
        this.$message.warning(
          "El centro médico todavía no configuró tipos de trámite. Comunicate con administración."
        );
        return;
      }
      this.newModal = {
        visible: true,
        saving: false,
        form: emptyNewForm(),
      };
    },
    closeNew() {
      this.newModal.visible = false;
    },

    /**
     * @description UC-REG-297 — Al cambiar la tipología se resetea el subtipo
     *   y la derivación (depende del tipo final).
     */
    onTypeChange() {
      this.newModal.form.procedureSubtype = "";
      this.newModal.form.referralFiles = [];
    },

    onFilesSelected(event) {
      this.newModal.form.files = Array.from(event.target.files || []);
    },
    removeFile(idx) {
      this.newModal.form.files.splice(idx, 1);
    },

    /**
     * @description UC-REG-299 — Input de la orden de derivación: solo fotos o
     *   PDF; lo demás se descarta con aviso.
     * @param {Event} event
     */
    onReferralSelected(event) {
      const picked = Array.from(event.target.files || []);
      const valid = picked.filter((f) => isReferralFile(f));
      if (valid.length < picked.length) {
        this.$message.warning(
          "La orden de derivación debe ser una foto (JPG/PNG) o un PDF"
        );
      }
      this.newModal.form.referralFiles = valid;
      if (valid.length === 0) event.target.value = "";
    },
    removeReferral(idx) {
      this.newModal.form.referralFiles.splice(idx, 1);
    },

    async submitNew() {
      const { form } = this.newModal;
      const resolved = resolveSelectedType({
        types: this.types,
        typeId: form.procedureType,
        subtypeId: form.procedureSubtype,
      });
      if (!resolved.ok) {
        this.$message.warning(resolved.error);
        return;
      }
      if (!this.preferredDoctorMode && !form.description.trim()) {
        this.$message.warning("Escribí una descripción del trámite");
        return;
      }
      if (this.needsReferral && form.referralFiles.length === 0) {
        this.$message.warning(
          "Este trámite requiere adjuntar la foto o el PDF de la orden de derivación médica"
        );
        return;
      }
      // UC-REG-316 — "Médico de preferencia" exige el nombre.
      if (
        this.wantsPreferredDoctor &&
        !normalizePreferredDoctor(form.preferredDoctor)
      ) {
        this.$message.warning("Escribí el nombre del médico de preferencia");
        return;
      }
      this.newModal.saving = true;
      try {
        const fd = new FormData();
        fd.append("procedureType", resolved.typeId);
        fd.append("description", form.description.trim());
        const preferredDoctor = normalizePreferredDoctor(form.preferredDoctor);
        // UC-REG-316 — con radios: la prioridad viaja y el médico solo si lo eligió.
        if (this.showPriority) {
          fd.append("appointmentPreference", form.appointmentPreference);
          if (this.wantsPreferredDoctor && preferredDoctor) {
            fd.append("preferredDoctor", preferredDoctor);
          }
        } else if (preferredDoctor) {
          fd.append("preferredDoctor", preferredDoctor);
        }
        form.referralFiles.forEach((f) => fd.append(REFERRAL_FIELD, f, f.name));
        form.files.forEach((f) => fd.append("files", f, f.name));
        await procedureService.create(fd);
        this.closeNew();
        await this.load();
        this.$message.success("Trámite enviado");
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo enviar el trámite"
        );
      } finally {
        this.newModal.saving = false;
      }
    },

    async openView(procedureRow) {
      try {
        const full = await procedureService.getById(procedureRow._id);
        this.viewModal = {
          visible: true,
          procedure: full,
          newMessage: "",
          newFiles: [],
          sending: false,
        };
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error("No se pudo abrir el trámite");
      }
    },
    closeView() {
      this.viewModal.visible = false;
    },

    /**
     * @description Handler del input file — acumula archivos en newFiles
     *              respetando el tope del backend (10 archivos / 15MB c/u).
     */
    onPickFiles(event) {
      const picked = Array.from(event.target.files || []);
      picked.forEach((f) => {
        if (f.size > 15 * 1024 * 1024) {
          this.$message.warning(`"${f.name}" supera los 15MB, no se adjunta`);
          return;
        }
        if (this.viewModal.newFiles.length >= 10) {
          this.$message.warning("Máximo 10 adjuntos por mensaje");
          return;
        }
        this.viewModal.newFiles.push(f);
      });
      // Permitir volver a elegir el mismo archivo si el usuario lo quita.
      event.target.value = "";
    },
    removeNewFile(index) {
      this.viewModal.newFiles.splice(index, 1);
    },

    /**
     * @description Envía un mensaje del submitter (texto y/o adjuntos).
     *              Backend actualiza status a `pending` y devuelve el doc
     *              populado.
     */
    async sendMessage() {
      const text = this.viewModal.newMessage.trim();
      const files = this.viewModal.newFiles;
      if (!text && files.length === 0) return;
      this.viewModal.sending = true;
      try {
        const updated = await procedureService.addMessage(
          this.viewModal.procedure._id,
          text,
          undefined,
          files
        );
        this.viewModal.procedure = updated;
        this.viewModal.newMessage = "";
        this.viewModal.newFiles = [];
        await this.load();
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo enviar el mensaje"
        );
      } finally {
        this.viewModal.sending = false;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    },

    scrollChatToBottom() {
      const el = document.querySelector(".my-proc__chat");
      if (el) el.scrollTop = el.scrollHeight;
    },

    async downloadFile(fileEntry) {
      try {
        const { url } = await procedureService.getFileSignedUrl(
          this.viewModal.procedure._id,
          fileEntry.key
        );
        window.open(url, "_blank");
      } catch (err) {
        this.$message.error("No se pudo abrir el adjunto");
      }
    },

    formatDate(date) {
      if (!date) return "—";
      try {
        return format(new Date(date), "dd/MM/yyyy HH:mm");
      } catch (e) {
        return "—";
      }
    },

    /**
     * @description UC-REG-297 — "Tipología / Subtipo" del trámite.
     * @param {Object} p - Procedure con procedureType populado
     * @returns {string}
     */
    typeName(p) {
      return typeLabel(p && p.procedureType, this.types) || "Trámite";
    },

    /**
     * @description UC-REG-298 — Primer mensaje: descripción o médico de
     *   preferencia.
     * @param {Object} p - Procedure
     * @returns {string}
     */
    requestText(p) {
      return requestText(p);
    },

    /**
     * @description UC-REG-299 — Nombre del adjunto con la marca "Derivación".
     * @param {Object} f - ProcedureFile
     * @returns {string}
     */
    fileLabel(f) {
      const kind = fileKindLabel(f);
      const name = (f && (f.originalName || f.key)) || "";
      return kind ? `${kind} · ${name}` : name;
    },

    /**
     * @description UC-REG-253 — Texto del turno asociado al trámite:
     *              "dd/MM/yyyy HH:mm · Dr. X · Sede" (UC-REG-262, Campana:
     *              "dd/MM/yyyy HH:mm · Institución", sin médico).
     * @param {Object} a - Appointment populado (doctor/institution)
     * @returns {string}
     */
    formatAppointment(a) {
      return formatProcedureAppointment(a, this.formatDate);
    },

    /**
     * @description UC-REG-253 — El trámite tiene un turno vigente (no
     *              cancelado ni atendido) para mostrarle al paciente.
     * @param {Object} p - Procedure con `appointment` populado
     * @returns {boolean}
     */
    hasActiveAppointment(p) {
      const a = p && p.appointment;
      return Boolean(
        a && ["pending", "admitted", "started"].includes(a.status)
      );
    },

    /**
     * @description UC-REG-300 — El turno del trámite abierto se puede
     *              cancelar (flag de Campana + turno vigente).
     * @param {Object} p - Procedure con `appointment` populado
     * @returns {boolean}
     */
    canCancelAppointment(p) {
      const a = p && p.appointment;
      if (!a) return false;
      return canCancelProcedureAppointment({ ...a, procedure: p._id });
    },

    /**
     * @description UC-REG-300 — Cancela el turno del trámite abierto con
     *              motivo opcional y refresca el detalle (mensaje automático
     *              en el chat) y la lista.
     */
    async cancelAppointment() {
      const procedure = this.viewModal.procedure;
      if (!procedure) return;
      let value = "";
      try {
        ({ value } = await this.$prompt(
          PROCEDURE_CANCEL_DIALOG.message,
          PROCEDURE_CANCEL_DIALOG.title,
          {
            confirmButtonText: PROCEDURE_CANCEL_DIALOG.confirm,
            cancelButtonText: PROCEDURE_CANCEL_DIALOG.cancel,
            inputPlaceholder: PROCEDURE_CANCEL_DIALOG.placeholder,
            type: "warning",
          }
        ));
      } catch (e) {
        return;
      }
      this.viewModal.sending = true;
      try {
        const updated = await procedureService.cancelAppointment(
          procedure._id,
          value
        );
        this.viewModal.procedure = updated;
        this.$message.success(PROCEDURE_CANCEL_DIALOG.success);
        await this.load();
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error(procedureCancelErrorMessage(err));
      } finally {
        this.viewModal.sending = false;
      }
    },

    statusLabel(status) {
      if (status === "closed") return "Cerrado";
      if (status === "answered") return "Respondido";
      return "Pendiente";
    },
    statusClass(status) {
      if (status === "closed") return "is-closed";
      if (status === "answered") return "is-answered";
      return "is-pending";
    },

    /**
     * @description True si el mensaje fue del paciente logueado (bubble
     *              alineado a la derecha).
     */
    isMineMessage(msg) {
      if (!msg || !msg.author) return false;
      const authorId =
        typeof msg.author === "string" ? msg.author : msg.author._id;
      return String(authorId) === String(this.user && this.user._id);
    },
  },
};
</script>

<template lang="pug">
.my-proc
  .my-proc__header
    h2.my-proc__title Mis trámites
    button.my-proc__new(@click="openNew") + Nuevo trámite
  p.my-proc__hint Enviá pedidos administrativos al centro médico. Un operador revisa y responde. Podés seguir conversando dentro de cada trámite hasta que se cierre.

  .my-proc__empty(v-if="!loading && procedures.length === 0")
    p Todavía no enviaste ningún trámite.
    p Usá "Nuevo trámite" para enviar tu primer pedido.

  .my-proc__list(v-if="procedures.length > 0")
    .my-proc__row(v-for="p in procedures" :key="p._id" @click="openView(p)")
      .my-proc__row-main
        .my-proc__row-type {{ typeName(p) }}
        .my-proc__row-desc {{ requestText(p) }}
        .my-proc__row-date Enviado el {{ formatDate(p.createdAt) }}
        //- UC-REG-253 — turno vigente asignado desde la respuesta del staff.
        .my-proc__row-appt(v-if="hasActiveAppointment(p)") 📅 Turno: {{ formatAppointment(p.appointment) }}
      .my-proc__row-status
        span.my-proc__badge(:class="statusClass(p.status)") {{ statusLabel(p.status) }}
        span.my-proc__row-arrow ›

  //- ═══ Modal Nuevo trámite ═══
  .my-proc__modal-backdrop(v-if="newModal.visible" @click.self="closeNew")
    .my-proc__modal
      h3 Nuevo trámite
      .my-proc__field
        label Tipo *
        select(v-model="newModal.form.procedureType" @change="onTypeChange")
          option(value="" disabled) Elegí un tipo
          option(v-for="t in topTypes" :key="t._id" :value="t._id") {{ t.name }}
      //- UC-REG-297 — subtipología (solo si la tipología elegida tiene subtipos).
      .my-proc__field(v-if="subtypes.length > 0")
        label Subtipo *
        select(v-model="newModal.form.procedureSubtype")
          option(value="" disabled) Elegí un subtipo
          option(v-for="t in subtypes" :key="t._id" :value="t._id") {{ t.name }}
      //- UC-REG-316 (Campana) — tipo con turno: qué prioriza el vecino.
      .my-proc__field.my-proc__field--priority(v-if="showPriority")
        label ¿Qué preferís priorizar para el turno? *
        .my-proc__radios
          label.my-proc__radio(v-for="o in appointmentPreferences" :key="o.value")
            input(type="radio" name="appointmentPreference" :value="o.value" v-model="newModal.form.appointmentPreference")
            span {{ o.label }}
        template(v-if="wantsPreferredDoctor")
          input(type="text" v-model="newModal.form.preferredDoctor" :maxlength="preferredDoctorMax" placeholder="Nombre del médico con el que preferís atenderte")
          p.my-proc__field-hint Si ese médico no tiene turnos pronto, el centro de salud te va a avisar.
      //- UC-REG-298 (Campana) — médico de preferencia en lugar de la descripción.
      .my-proc__field(v-else-if="preferredDoctorMode")
        label Médico de preferencia (opcional)
        input(type="text" v-model="newModal.form.preferredDoctor" :maxlength="preferredDoctorMax" placeholder="Profesional de cabecera con el que preferís atenderte")
      .my-proc__field(v-else)
        label Descripción *
        textarea(v-model="newModal.form.description" rows="5" maxlength="2000" placeholder="Contá el motivo del trámite, datos relevantes, etc.")
      //- UC-REG-299 — orden de derivación obligatoria según el tipo.
      .my-proc__field.my-proc__field--referral(v-if="needsReferral")
        label Orden de derivación médica *
        p.my-proc__field-hint Sacale una foto a la orden de derivación o adjuntá el PDF. Sin la orden no se puede enviar este trámite.
        input(type="file" :accept="referralAccept" capture="environment" multiple @change="onReferralSelected")
        ul.my-proc__files(v-if="newModal.form.referralFiles.length > 0")
          li(v-for="(f, idx) in newModal.form.referralFiles" :key="'r' + idx")
            span 📎 {{ f.name }} ({{ Math.round(f.size / 1024) }} KB)
            button.my-proc__file-remove(@click="removeReferral(idx)") ×
      .my-proc__field
        label Adjuntos (opcional)
        input(type="file" multiple @change="onFilesSelected")
        ul.my-proc__files(v-if="newModal.form.files.length > 0")
          li(v-for="(f, idx) in newModal.form.files" :key="idx")
            span {{ f.name }} ({{ Math.round(f.size / 1024) }} KB)
            button.my-proc__file-remove(@click="removeFile(idx)") ×
      .my-proc__modal-actions
        button.my-proc__btn.my-proc__btn--secondary(@click="closeNew" :disabled="newModal.saving") Cancelar
        button.my-proc__btn.my-proc__btn--primary(@click="submitNew" :disabled="newModal.saving")
          | {{ newModal.saving ? "Enviando…" : "Enviar trámite" }}

  //- ═══ Modal Ver / Chat ═══
  .my-proc__modal-backdrop(v-if="viewModal.visible" @click.self="closeView")
    .my-proc__modal.my-proc__modal--chat(v-if="viewModal.procedure")
      .my-proc__modal-header
        h3 {{ typeName(viewModal.procedure) }}
        span.my-proc__badge(:class="statusClass(viewModal.procedure.status)") {{ statusLabel(viewModal.procedure.status) }}
      p.my-proc__view-meta Enviado el {{ formatDate(viewModal.procedure.createdAt) }}
      //- UC-REG-316 — prioridad del turno; UC-REG-298 — médico de preferencia.
      p.my-proc__view-meta(v-if="priorityText(viewModal.procedure)") {{ priorityText(viewModal.procedure) }}
      p.my-proc__view-meta(v-else-if="viewModal.procedure.preferredDoctor") Médico de preferencia: {{ viewModal.procedure.preferredDoctor }}
      //- UC-REG-253 — bloque de turno asignado (visible mientras esté vigente).
      .my-proc__appt(v-if="hasActiveAppointment(viewModal.procedure)")
        .my-proc__appt-title 📅 Turno asignado
        .my-proc__appt-when {{ formatAppointment(viewModal.procedure.appointment) }}
        //- UC-REG-300 — cancelación desde el trámite (flag de Campana).
        template(v-if="canCancelAppointment(viewModal.procedure)")
          .my-proc__appt-hint Vas a recibir un recordatorio el día anterior. También lo ves en "Turnos". Si no podés asistir, cancelalo acá; para reprogramar, escribí abajo.
          button.my-proc__btn.my-proc__btn--danger.my-proc__appt-cancel(type="button" @click="cancelAppointment" :disabled="viewModal.sending") Cancelar turno
        .my-proc__appt-hint(v-else) Vas a recibir un recordatorio el día anterior. También lo ves en "Turnos". Si no podés asistir, avisá acá abajo.

      .my-proc__chat
        .my-proc__msg.my-proc__msg--mine
          .my-proc__msg-bubble
            .my-proc__msg-text {{ requestText(viewModal.procedure) }}
            .my-proc__msg-files(v-if="viewModal.procedure.files && viewModal.procedure.files.length > 0")
              a.my-proc__msg-file(v-for="(f, i) in viewModal.procedure.files" :key="i" href="#" @click.prevent="downloadFile(f)")
                | 📎 {{ fileLabel(f) }}
          .my-proc__msg-meta {{ formatDate(viewModal.procedure.createdAt) }}

        .my-proc__msg(v-for="(m, idx) in viewModal.procedure.messages" :key="idx" :class="{ 'my-proc__msg--mine': isMineMessage(m), 'my-proc__msg--other': !isMineMessage(m) }")
          .my-proc__msg-author(v-if="!isMineMessage(m)")
            | {{ m.author && ((m.author.firstName || '') + ' ' + (m.author.lastName || '')).trim() || 'Centro médico' }}
          .my-proc__msg-bubble
            .my-proc__msg-text(v-if="m.text") {{ m.text }}
            .my-proc__msg-files(v-if="m.files && m.files.length > 0")
              a.my-proc__msg-file(v-for="(f, i) in m.files" :key="i" href="#" @click.prevent="downloadFile(f)")
                | 📎 {{ f.originalName || f.key }}
          .my-proc__msg-meta {{ formatDate(m.createdAt) }}

      .my-proc__compose-block(v-if="viewModal.procedure.status !== 'closed'")
        .my-proc__compose
          textarea(
            v-model="viewModal.newMessage"
            rows="2"
            maxlength="4000"
            placeholder="Escribí tu mensaje…"
            :disabled="viewModal.sending"
          )
          button.my-proc__btn.my-proc__btn--primary(@click="sendMessage" :disabled="viewModal.sending || (!viewModal.newMessage.trim() && viewModal.newFiles.length === 0)")
            | {{ viewModal.sending ? "Enviando…" : "Enviar" }}
        //- Fila de adjuntos: botón + chips de archivos ya elegidos.
        .my-proc__attach-row
          label.my-proc__attach-btn(:class="{ 'is-disabled': viewModal.sending }")
            micon(name="attach_file")
            span Adjuntar
            input(type="file" multiple @change="onPickFiles" :disabled="viewModal.sending" hidden)
          .my-proc__attach-chips(v-if="viewModal.newFiles.length > 0")
            .my-proc__attach-chip(v-for="(f, i) in viewModal.newFiles" :key="i")
              span 📎 {{ f.name }} ({{ formatFileSize(f.size) }})
              button(type="button" @click="removeNewFile(i)" :disabled="viewModal.sending") ×
      .my-proc__closed-note(v-else)
        | Este trámite fue cerrado por el centro médico. No se pueden agregar más mensajes.

      .my-proc__modal-actions
        button.my-proc__btn.my-proc__btn--secondary(@click="closeView") Cerrar ventana
</template>

<style lang="scss" scoped>
.my-proc {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}
.my-proc__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.my-proc__title {
  margin: 0;
  font-size: 20px;
  color: $navy;
}
.my-proc__new {
  background: $navy;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: darken($navy, 5%);
  }
}
.my-proc__hint {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 20px 0;
  line-height: 1.4;
}
.my-proc__empty {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 13px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  p {
    margin: 6px 0;
  }
}
.my-proc__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.my-proc__row {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  }
}
.my-proc__row-main {
  flex: 1;
  min-width: 0;
}
.my-proc__row-type {
  font-size: 14px;
  font-weight: 700;
  color: $navy;
  margin-bottom: 2px;
}
.my-proc__row-desc {
  font-size: 12.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}
.my-proc__row-date {
  font-size: 11px;
  color: #64748b;
}
.my-proc__row-status {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.my-proc__row-arrow {
  color: #cbd5e1;
  font-size: 20px;
}
.my-proc__badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  &.is-pending {
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
  }
  &.is-answered {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }
  &.is-closed {
    background: #f1f5f9;
    color: #64748b;
  }
}
.my-proc__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.my-proc__modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  h3 {
    margin: 0 0 12px 0;
    font-size: 16px;
    color: $navy;
  }
  &--chat {
    max-width: 620px;
    display: flex;
    flex-direction: column;
  }
}
.my-proc__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  h3 {
    margin: 0;
  }
}
.my-proc__view-meta {
  font-size: 11px;
  color: #64748b;
  margin: 0 0 12px 0;
}
.my-proc__field {
  margin-bottom: 16px;
  label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 6px;
  }
  select,
  textarea,
  input[type="text"],
  input[type="file"] {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    background: white;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
  &--referral {
    border: 1px solid #fde68a;
    background: #fffbeb;
    border-radius: 8px;
    padding: 12px;
  }
}
.my-proc__field-hint {
  margin: -2px 0 8px 0;
  font-size: 11.5px;
  color: #92400e;
  line-height: 1.4;
}
.my-proc__files {
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
    font-size: 12.5px;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
  }
}
.my-proc__file-remove {
  background: none;
  border: none;
  color: #64748b;
  font-size: 16px;
  cursor: pointer;
  padding: 0 8px;
  &:hover {
    color: #dc2626;
  }
}

/* Chat */
.my-proc__chat {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.my-proc__msg {
  display: flex;
  flex-direction: column;
  max-width: 78%;
  &--mine {
    align-self: flex-end;
    align-items: flex-end;
    .my-proc__msg-bubble {
      background: $navy;
      color: white;
    }
    .my-proc__msg-file {
      color: white;
      text-decoration: underline;
    }
  }
  &--other {
    align-self: flex-start;
    align-items: flex-start;
    .my-proc__msg-bubble {
      background: white;
      color: $navy;
      border: 1px solid #e2e8f0;
    }
  }
}
.my-proc__msg-author {
  font-size: 10.5px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 3px;
  padding: 0 4px;
}
.my-proc__msg-bubble {
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
}
.my-proc__msg-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.my-proc__msg-files {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.my-proc__msg-file {
  font-size: 12px;
  color: $navy;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
.my-proc__msg-meta {
  font-size: 10px;
  color: #64748b;
  margin-top: 3px;
  padding: 0 4px;
}
.my-proc__compose-block {
  margin-bottom: 12px;
}
.my-proc__compose {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  textarea {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
  button {
    height: 42px;
    padding: 0 18px;
  }
}
.my-proc__attach-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
  padding: 4px 2px 0;
}
.my-proc__attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  color: #334155;
  background: white;
  cursor: pointer;
  &:hover {
    border-color: $navy;
    color: $navy;
  }
  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.my-proc__attach-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.my-proc__attach-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 12px;
  font-size: 11px;
  color: #334155;
  button {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0 2px;
    &:hover {
      color: #dc2626;
    }
  }
}
.my-proc__closed-note {
  text-align: center;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}
.my-proc__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}
.my-proc__btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &--primary {
    background: $navy;
    color: white;
    &:hover:not(:disabled) {
      background: darken($navy, 5%);
    }
  }
  &--secondary {
    background: #f1f5f9;
    color: #334155;
    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  }
  &--danger {
    background: #fee2e2;
    color: #b91c1c;
    &:hover:not(:disabled) {
      background: #fecaca;
    }
  }
}
.my-proc__appt-cancel {
  margin-top: 8px;
  padding: 6px 12px;
  font-size: 12px;
}

/* ═══ UC-REG-253 — turno asociado al trámite ═══ */
.my-proc__row-appt {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
}
.my-proc__appt {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.my-proc__appt-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1e3a8a;
  margin-bottom: 4px;
}
.my-proc__appt-when {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.my-proc__appt-hint {
  margin-top: 4px;
  font-size: 11px;
  color: #475569;
  line-height: 1.4;
}
</style>
